import { jsPDF } from "jspdf"; 
import autoTable from "jspdf-autotable"; // npm i jspdf jspdf-autotable
import { box, text, grid, boxCustom, line, textWrap, labelValue, addAnuladoWatermark } from "../pdfDraw";
import { formatDate, formatDecimal } from "../../formatters";
import { getRetencionIVA } from "../../../controllers/apiWithholdings";
import { getClientLogo } from "../../../controllers/api_clients";
import { format } from "crypto-js";
import QRCode from "qrcode";
import { encryptText } from '../../../controllers/api';

/* ================= BUILD PDF ================= */
export const buildIVAPDF = async (comprobante_id, withholdingId, mode = "download") => {
  // Obtener datos desde la API
  const data = await getRetencionIVA(withholdingId);
  if (!data) return;

  if (data.agente_retencion.logo_url){
    var empresa_id = data.agente_retencion.logo_url.split('/')[3]
    data.agente_retencion.logo_base64 = await getClientLogo(empresa_id);
  }

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "letter" });
  const g = grid();
  let y = 12;
  // (2026-05-18) Cabecera comprimida: cajas más bajas (15->12) para dar más
  // espacio a la tabla de retenciones. Etiqueta en y+4, valor en y+11
  // (sigue dentro de la caja de 12).
  const h = 12;
  const vGap = g.vGap;

  const cabecera = data.cabecera;
  const agente = data.agente_retencion;
  const sujeto = data.sujeto_retenido;
  const detalle = data.detalle || [];
  const totales = data.totales;
  const firmas = data.firmas;
  const legal = data.legal;
  const tercero = data.tercero; // (2026-05-18 ADJ-1) ventas por cuenta de terceros (puede ser null)

  /* ================= PRIMERA FILA CENTRADA ================= */
  const col12 = g.col(0, 12);
  let yTitle = y;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("COMPROBANTE DE RETENCIÓN DEL IMPUESTO AL VALOR AGREGADO I.V.A", col12.x + col12.w/2, yTitle, { align: "center" });
  yTitle += 6;

  doc.setFontSize(8);
  doc.text(legal.providencia, col12.x + col12.w/2, yTitle, { align: "center" });
  yTitle += 6;

  doc.setFontSize(6);
  doc.setFont("helvetica", "normal");
  if (legal.articulo) {
    doc.text(legal.articulo, col12.x + col12.w/2, yTitle, { maxWidth: col12.w, align: "center" });
  }

  /* ---------- QR ---------- */

  const baseUrl = window.location.origin;

  const qrUrl = `${baseUrl}/document/RETIVA/${encodeURIComponent(encryptText(cabecera.qr_token.toString()))}/`;

  const qrBase64 = await QRCode.toDataURL(qrUrl, {
    width: 120,
    margin: 1
  });
  const qrSize = 23;
  /*
   * @param {object} doc - Instancia de jsPDF
   * @param {number} x - Posición X inicial
   * @param {number} y - Posición Y inicial
   * @param {number} w - Ancho de la caja
   * @param {number} h - Alto de la caja
   * @param {string|array} fillColor - Color de fondo (hex o RGB) (default "#FFFFFF")
   * @param {number} borderRadius - Radio de los bordes (default 0)
   * @param {number} borderWidth - Grosor del borde en mm (default 0, sin borde)
   * @param {string|array} borderColor - Color del borde (hex o RGB) (default "#000000")
  */
  boxCustom(doc, (col12.w - 15), (y - 6), qrSize + 2, qrSize + 2, "#ffffff", 2, 0.5, "#4a4a4a");
  doc.addImage(
    qrBase64,
    "PNG",
    col12.w - 14,
    y - 5,
    qrSize,
    qrSize
  );

  // (2026-05-18) Inicio de la cabecera con margen suficiente para NO
  // solapar el QR (top-right). +5 lo subía demasiado.
  y = yTitle + 9;

  /* ================= NRO COMPROBANTE Y FECHA ================= */
  const col7 = g.col(0, 7);
  const col5 = g.col(7, 5);
  const boxWidth = 50;
  const boxHeight = 12;
  const gap = 5;

  const startX = col5.x;
  box(doc, startX + 5.3, y, boxWidth, boxHeight);
  text(doc, "0. NRO. COMPROBANTE", startX + 7.3, y + 4, 7);
  text(doc, cabecera.numero_comprobante, (startX + boxWidth / 2) + 5.3, y + 9, 9, "center");

  const x2 = startX + boxWidth + gap;
  box(doc, x2 + 2.4, y, boxWidth, boxHeight);
  text(doc, "1. FECHA EMISIÓN", x2 + 4.4, y + 4, 7);
  text(doc, formatDate(cabecera.fecha_emision), (x2 + boxWidth / 2) + 2.4, y + 9, 9, "center");

  // (2026-07-18 A3/A4) Nº de Control PROPIO del comprobante, destacado en rojo/
  // bold (estilo consistente con facturas: el control va en rojo/bold). Se OMITE
  // si el backend no lo entrega (comprobantes históricos sin control del lote).
  if (cabecera.numero_control) {
    doc.setTextColor(220, 38, 38); // tailwind red-600
    text(doc, `Nro. de Control: ${cabecera.numero_control}`, col7.x + 2, y + 8, 10, "left", true);
    doc.setTextColor(0, 0, 0); // reset a negro
  }

  y += boxHeight + vGap;

  /* ================= DATOS AGENTE Y SUJETO ================= */
  const colAgente = g.col(0, 5);
  const colRif = g.col(5, 5);
  const colPeriodo = g.col(10, 2);

  box(doc, colAgente.x, y, colAgente.w, h);
  text(doc, "2. NOMBRE O RAZÓN SOCIAL DEL AGENTE DE RETENCIÓN", colAgente.x + 2, y + 4, 7);
  text(doc, agente.nombre_razon_social, colAgente.x + 2, y + 9);

  box(doc, colRif.x, y, colRif.w, h);
  text(doc, "3. REGISTRO DE INFORMACIÓN FISCAL DEL AGENTE DE RETENCIÓN", colRif.x + 2, y + 4, 7);
  text(doc, agente.rif, colRif.x + 2, y + 9);

  box(doc, colPeriodo.x, y, colPeriodo.w, h);
  text(doc, "4. PERIODO FISCAL", colPeriodo.x + 2, y + 4, 7);
  text(doc, `AÑO: ${cabecera.anio_fiscal} / MES: ${cabecera.mes_fiscal}`, colPeriodo.x + colPeriodo.w / 2, y + 9, 9, "center");

  y += h + vGap;

  // Dirección agente
  const colDirAgente = g.col(0, 12);
  box(doc, colDirAgente.x, y, colDirAgente.w, h - 5);
  text(doc, "5. DIRECCIÓN FISCAL DEL AGENTE DE RETENCIÓN: " + (agente.direccion_fiscal || ""), colDirAgente.x + 2, y + 5);
  // (2026-05-18) Avance acorde a la caja real (h-5), antes y+=h desperdiciaba 5mm.
  y += (h - 5) + vGap;

  // (2026-05-18) Si la retención proviene de una factura de TERCEROS, el
  // bloque del tercero ocupa las casillas 6/7/8 y el Sujeto Retenido se
  // RENUMERA a 9/10/11. Si no hay tercero, el Sujeto Retenido conserva
  // 6/7/8 (sin bloque de terceros). _tOff = desplazamiento de numeración.
  const _tOff = tercero ? 3 : 0;

  /* ====== VENTAS POR CUENTA DE TERCEROS (casillas 6,7,8 — solo si aplica)
     Ubicado ANTES de la sección de Sujeto Retenido. */
  if (tercero) {
    const colTcab = g.col(0, 12);
    box(doc, colTcab.x, y, colTcab.w, 6);
    text(doc, "VENTAS POR CUENTA DE TERCEROS", colTcab.x + 2, y + 4, 7);
    y += 6;
    const colTn = g.col(0, 7);
    const colTr = g.col(7, 5);
    box(doc, colTn.x, y, colTn.w, h);
    text(doc, "6. NOMBRE / RAZÓN SOCIAL DEL TERCERO", colTn.x + 2, y + 4, 7);
    text(doc, (tercero.nombre_empresa || tercero.denominacion_comercial || ""), colTn.x + 2, y + 9);
    box(doc, colTr.x, y, colTr.w, h);
    text(doc, "7. R.I.F. DEL TERCERO", colTr.x + 2, y + 4, 7);
    text(doc, (tercero.rif || ""), colTr.x + 2, y + 9);
    y += h + vGap;
    const colTd = g.col(0, 12);
    box(doc, colTd.x, y, colTd.w, h - 5);
    text(doc, "8. DIRECCIÓN FISCAL DEL TERCERO: " + (tercero.direccion_fiscal || ""), colTd.x + 2, y + 5);
    // (2026-05-18) Avance acorde a la caja real (h-5).
    y += (h - 5) + vGap;
  }

  // Sujeto retenido (casillas 6/7/8 normal, o 9/10/11 si hay tercero)
  const colSujeto = g.col(0, 6);
  const colRifSujeto = g.col(6, 6);
  box(doc, colSujeto.x, y, colSujeto.w, h);
  text(doc, `${6 + _tOff}. NOMBRE SUJETO RETENIDO`, colSujeto.x + 2, y + 4, 7);
  text(doc, sujeto.nombre_razon_social, colSujeto.x + 2, y + 9);

  box(doc, colRifSujeto.x, y, colRifSujeto.w, h);
  text(doc, `${7 + _tOff}. REGISTRO DE INFORMACIÓN FISCAL DEL SUJETO RETENIDO (R.I.F.)`, colRifSujeto.x + 2, y + 4, 7);
  text(doc, sujeto.rif, colRifSujeto.x + 2, y + 9);
  y += h + vGap;

  // Dirección sujeto
  const colDirSujeto = g.col(0, 12);
  box(doc, colDirSujeto.x, y, colDirSujeto.w, h - 5);
  text(doc, `${8 + _tOff}. DIRECCIÓN FISCAL DEL SUJETO RETENIDO: ` + (sujeto.direccion_fiscal || ""), colDirSujeto.x + 2, y + 5);
  // (2026-05-18) Avance acorde a la caja real (h-5).
  y += (h - 5) + vGap;

  /* ================= TABLA DE RETENCIONES ================= */
  const tableData = detalle.map(d => [
    d.oper_nro,
    formatDate(d.fecha),
    d.nro_factura,
    d.nro_control,
    d.nro_nota_debito,
    d.nro_nota_credito,
    d.tipo_transaccion,
    d.nro_documento_afectado,
    formatDecimal(d.total_compras_con_iva),
    formatDecimal(d.compras_sin_derecho_a_credito),
    formatDecimal(d.base_imponible),
    d.porcentaje_alicuota,
    formatDecimal(d.impuesto_iva),
    formatDecimal(d.iva_retenido)
  ]);

  autoTable(doc, {
    startY: y,
    head: [[
        "Oper N°",
        "Fecha",
        "N° Factura",
        "N° Control",
        "N° Nota de Débito",
        "N° Nota de Crédito",
        "Tipo de Trans.",
        "N° Documento Afectado",
        "Total Compras con I.V.A.",
        "Compras sin Derecho a Crédito",
        "Base Imponible",
        "% Alic.",
        "Impuesto I.V.A.",
        "I.V.A. Retenido"
    ]],
    body: tableData,
    foot: [[
        "", 
        "", 
        "", 
        "", 
        "", 
        "", 
        "", 
        "", 
        formatDecimal(totales.total_compras_con_iva),
        formatDecimal(totales.compras_sin_derecho_a_credito),
        formatDecimal(totales.base_imponible), 
        "", 
        formatDecimal(totales.impuesto_iva),
        formatDecimal(totales.iva_retenido)
    ]],
    theme: 'grid',
    styles: { font: "helvetica", fontSize: 8 },
    headStyles: { fillColor: [220,220,220], textColor:0, fontStyle:"bold", halign:"center", valign:"middle" },
    footStyles: { fillColor:[240,240,240], textColor:0, fontStyle:"bold", halign:"right" },
    columnStyles: { 8: {halign:'right'}, 9:{halign:'right'}, 10:{halign:'right'}, 11:{halign:'center'}, 12:{halign:'right'}, 13:{halign:'right'} },
    margin: { left: g.col(0,12).x, right: 10 },
    tableWidth: g.col(0,12).w
  });

  // (2026-07-18 A3/A4) Pie de imprenta (leyenda_legal del lote), debajo de la
  // tabla. Se imprime verbatim (normal) SOLO si el backend lo entrega.
  if (legal && legal.leyenda_legal) {
    const _legalY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : y) + 6;
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    doc.text(legal.leyenda_legal, g.col(0,12).x + 2, _legalY, { maxWidth: g.col(0,12).w, align: "left" });
  }

  /* ================= PIE DE PÁGINA ================= */
  const footerHeight = 20;
  const footerTop = doc.internal.pageSize.height - footerHeight;
  const colFirmaAgente = g.col(0,6);
  const colFechaEntrega = g.col(6,6);
  const colComprobante = g.col(0,12);
  const lineHeight = 6;

  // Firmas en una misma línea
  const yFirmas = footerTop + 2;
  text(doc, "Firma del Agente de Retención: _________________________", colFirmaAgente.x + 2, yFirmas, 8);
  text(doc, "Firma del Sujeto Retenido: _________________________", colFechaEntrega.x + 2, yFirmas, 8);

  // Fechas alineadas debajo de cada firma
  const yFechas = yFirmas + lineHeight;
  text(doc, `Fecha de Entrega: ${formatDate(firmas.fecha_entrega) || "_____/_____/______"}`, colFirmaAgente.x + 2, yFechas, 8);
  text(doc, `Fecha de Recepción: ${formatDate(firmas.fecha_recepcion) || "_____/_____/______"}`, colFechaEntrega.x + 2, yFechas, 8);

  // Texto legal en negrita debajo de las firmas
  doc.setFontSize(7);
  doc.setFont("helvetica","bold");
  doc.text(
    legal.articulo,
    colComprobante.x + 2,
    yFechas + lineHeight + 2,
    { maxWidth: colComprobante.w, align:"left" }
  );

  /* ================= GUARDAR PDF ================= */
  // (2026-07-08) Sello "ANULADO" en todas las páginas si el comprobante está anulado.
  if (cabecera.estatus === "anulado" || cabecera.estatus === "anulada") {
    const _pc = doc.getNumberOfPages();
    for (let _p = 1; _p <= _pc; _p++) { doc.setPage(_p); addAnuladoWatermark(doc, cabecera.fecha_anulacion); }
  }
  const pdfBlob = doc.output("blob");
  if(mode === "download"){
    doc.save(`RET_IVA_${cabecera.numero_comprobante}.pdf`);
  } else {
    return URL.createObjectURL(pdfBlob);
  }
};